import Big from 'big.js';
import React from 'react';
import { observer } from 'mobx-react';

import { FieldSet } from 'app/component/fieldset/FieldSet';
import { LimitFormItem } from 'app/logic/pages/productParametersConfigure/LimitFormItem';
import { FormatUtils } from 'app/utils/FormatUtils';
import { CardProduct } from 'app/model/product/CardProduct';
import { Section } from 'app/component/Section';
import { ProductParametersConfigureFormStore } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigureFormStore';
import { MonthlyPayment } from 'app/logic/pages/productParametersConfigure/MonthlyPayment';

export interface LimitFormProps {
    cardProduct: CardProduct;
    formStore: ProductParametersConfigureFormStore;
    maxAmount: Big;
}

/**
 * Расчет суммы ежемесячного платежа производится по формуле:
 * pay = summa * (ar2 / 1200 + mr2 / 100)
 * Сумма округляется до большего значения, без сохранения знаков после запятой.
 *
 * @param summa выбранная пользователем желаемая сумма кредитного лимита (динамический параметр)
 * @param ar2 годовая процентная  ставка по кредиту
 * @param mr2 процент от суммы задолженности по основному долгу
 */
const getMonthlyPayment = (summa: Big, ar2: Big, mr2: Big): Big =>
    summa.times(ar2.div(1200).plus(mr2.div(100))).round(0, 3);

export const LimitForm = observer(
    ({ cardProduct, formStore, maxAmount }: LimitFormProps) => {
        const isMonthlyPaymentCountable = formStore.creditLimit !== '';
        const monthlyPaymentBig =
            isMonthlyPaymentCountable &&
            getMonthlyPayment(
                new Big(formStore.creditLimit),
                cardProduct.rate,
                cardProduct.minInterestRate,
            );
        const monthlyPayment = isMonthlyPaymentCountable
            ? FormatUtils.toIntegerString(monthlyPaymentBig)
            : '';

        const defaultMinAmount = cardProduct.minAmount;
        const minAmount = formStore.hasAppliedInsurance && formStore.insuranceProduct
            ? new Big(formStore.insuranceProduct.minCreditAmount)
            : defaultMinAmount;

        return (
            <Section
                className="limitFormSection"
                content={
                    <>
                        <FieldSet
                            className={'limitForm'}
                            fieldsProps={[
                                {
                                    className: 'limit',
                                    title: '',
                                    value: (
                                        <LimitFormItem
                                            minAmount={minAmount}
                                            maxAmount={maxAmount}
                                            value={formStore.creditLimit}
                                            currency={cardProduct.currency}
                                            setValue={formStore.setCreditLimit.bind(
                                                formStore,
                                            )}
                                        />
                                    ),
                                },
                            ]}
                        />
                        <MonthlyPayment
                            currency={cardProduct.currency}
                            value={monthlyPayment}
                        />
                    </>
                }
            />
        );
    },
);
